import React, { useState } from 'react';
import { Check, Zap, Crown } from 'lucide-react';
import { PLAN_DETAILS } from '../../lib/constants';
import PaystackButton from './PaystackButton';

const PricingCards = ({ onSuccess }) => {
  const [selectedPlan, setSelectedPlan] = useState('MONTHLY');

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-brown-900 mb-2">
          Choose Your Plan
        </h3>
        <p className="text-sm sm:text-base text-brown-600">
          Select the plan that works best for you
        </p>
      </div>

      {/* Pricing Grid - Responsive */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
        
        {/* Weekly Plan */}
        <div 
          onClick={() => setSelectedPlan('WEEKLY')}
          className={`relative cursor-pointer backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl border-2 p-4 sm:p-5 md:p-6 transition-all hover:shadow-lg ${
            selectedPlan === 'WEEKLY' 
              ? 'border-brown-500 shadow-lg scale-105' 
              : 'border-cream-300/50'
          }`}
        >
          {/* Radio indicator */}
          <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
            <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center ${
              selectedPlan === 'WEEKLY' 
                ? 'border-brown-500 bg-brown-500' 
                : 'border-cream-300'
            }`}>
              {selectedPlan === 'WEEKLY' && (
                <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              )}
            </div>
          </div>

          <div className="mb-4 sm:mb-5">
            <h4 className="text-lg sm:text-xl font-bold text-brown-900 mb-1">
              {PLAN_DETAILS.WEEKLY.name}
            </h4>
            <p className="text-xs sm:text-sm text-brown-600">
              {PLAN_DETAILS.WEEKLY.description}
            </p>
          </div>

          <div className="mb-4 sm:mb-5">
            <div className="flex items-baseline">
              <span className="text-3xl sm:text-4xl font-bold text-brown-900">
                ₦{PLAN_DETAILS.WEEKLY.price.toLocaleString()}
              </span>
              <span className="text-sm sm:text-base text-brown-600 ml-2">
                /week
              </span>
            </div>
            <p className="text-xs sm:text-sm text-brown-500 mt-1">
              7 days of premium access
            </p>
          </div>

          <ul className="space-y-2 sm:space-y-2.5 mb-5 sm:mb-6">
            <li className="flex items-start space-x-2 text-xs sm:text-sm text-brown-700">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Unlimited uploads</span>
            </li>
            <li className="flex items-start space-x-2 text-xs sm:text-sm text-brown-700">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>AI summaries & quizzes</span>
            </li>
            <li className="flex items-start space-x-2 text-xs sm:text-sm text-brown-700">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>PDF export</span>
            </li>
          </ul>
        </div>

        {/* Monthly Plan - POPULAR */}
        <div 
          onClick={() => setSelectedPlan('MONTHLY')}
          className={`relative cursor-pointer backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl border-2 p-4 sm:p-5 md:p-6 transition-all hover:shadow-lg ${
            selectedPlan === 'MONTHLY' 
              ? 'border-brown-500 shadow-lg scale-105' 
              : 'border-cream-300/50'
          }`}
        >
          {/* Popular badge */}
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold rounded-full shadow-md">
              <Crown className="w-3 h-3 mr-1" />
              MOST POPULAR
            </span>
          </div>

          {/* Radio indicator */}
          <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
            <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center ${
              selectedPlan === 'MONTHLY' 
                ? 'border-brown-500 bg-brown-500' 
                : 'border-cream-300'
            }`}>
              {selectedPlan === 'MONTHLY' && (
                <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              )}
            </div>
          </div>

          <div className="mb-4 sm:mb-5 mt-2">
            <h4 className="text-lg sm:text-xl font-bold text-brown-900 mb-1">
              {PLAN_DETAILS.MONTHLY.name}
            </h4>
            <p className="text-xs sm:text-sm text-brown-600">
              {PLAN_DETAILS.MONTHLY.description}
            </p>
          </div>

          <div className="mb-4 sm:mb-5">
            <div className="flex items-baseline">
              <span className="text-3xl sm:text-4xl font-bold text-brown-900">
                ₦{PLAN_DETAILS.MONTHLY.price.toLocaleString()}
              </span>
              <span className="text-sm sm:text-base text-brown-600 ml-2">
                /month
              </span>
            </div>
            <p className="text-xs sm:text-sm text-green-600 font-semibold mt-1">
              {PLAN_DETAILS.MONTHLY.savings} compared to weekly
            </p>
          </div>

          <ul className="space-y-2 sm:space-y-2.5 mb-5 sm:mb-6">
            <li className="flex items-start space-x-2 text-xs sm:text-sm text-brown-700">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Unlimited uploads</span>
            </li>
            <li className="flex items-start space-x-2 text-xs sm:text-sm text-brown-700">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>AI summaries & quizzes</span>
            </li>
            <li className="flex items-start space-x-2 text-xs sm:text-sm text-brown-700">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>PDF export</span>
            </li>
            <li className="flex items-start space-x-2 text-xs sm:text-sm text-brown-700">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Priority support</span>
            </li>
          </ul>
        </div>

        {/* Semester Plan - BEST VALUE */}
        <div 
          onClick={() => setSelectedPlan('SEMESTER')}
          className={`relative cursor-pointer backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl border-2 p-4 sm:p-5 md:p-6 transition-all hover:shadow-lg ${
            selectedPlan === 'SEMESTER' 
              ? 'border-brown-500 shadow-lg scale-105' 
              : 'border-cream-300/50'
          }`}
        >
          {/* Best value badge */}
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-bold rounded-full shadow-md">
              <Zap className="w-3 h-3 mr-1" />
              BEST VALUE
            </span>
          </div>

          {/* Radio indicator */}
          <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
            <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center ${
              selectedPlan === 'SEMESTER' 
                ? 'border-brown-500 bg-brown-500' 
                : 'border-cream-300'
            }`}>
              {selectedPlan === 'SEMESTER' && (
                <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              )}
            </div>
          </div>

          <div className="mb-4 sm:mb-5 mt-2">
            <h4 className="text-lg sm:text-xl font-bold text-brown-900 mb-1">
              {PLAN_DETAILS.SEMESTER.name}
            </h4>
            <p className="text-xs sm:text-sm text-brown-600">
              {PLAN_DETAILS.SEMESTER.description}
            </p>
          </div>

          <div className="mb-4 sm:mb-5">
            <div className="flex items-baseline">
              <span className="text-3xl sm:text-4xl font-bold text-brown-900">
                ₦{PLAN_DETAILS.SEMESTER.price.toLocaleString()}
              </span>
              <span className="text-sm sm:text-base text-brown-600 ml-2">
                /4 months
              </span>
            </div>
            <p className="text-xs sm:text-sm text-green-600 font-semibold mt-1">
              {PLAN_DETAILS.SEMESTER.savings} compared to monthly
            </p>
          </div>

          <ul className="space-y-2 sm:space-y-2.5 mb-5 sm:mb-6">
            <li className="flex items-start space-x-2 text-xs sm:text-sm text-brown-700">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Unlimited uploads</span>
            </li>
            <li className="flex items-start space-x-2 text-xs sm:text-sm text-brown-700">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>AI summaries & quizzes</span>
            </li>
            <li className="flex items-start space-x-2 text-xs sm:text-sm text-brown-700">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>PDF export</span>
            </li>
            <li className="flex items-start space-x-2 text-xs sm:text-sm text-brown-700">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Priority support</span>
            </li>
            <li className="flex items-start space-x-2 text-xs sm:text-sm text-brown-700">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Study materials archive</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Checkout Button */}
      <div className="text-center pt-4 sm:pt-6">
        <PaystackButton 
          planType={selectedPlan}
          onSuccess={onSuccess}
          className="w-full sm:w-auto"
        />
      </div>

      {/* Info text */}
      <p className="text-xs sm:text-sm text-center text-brown-600">
        Secure payment powered by Paystack • One-time payment • No hidden automatic charges
      </p>
    </div>
  );
};

export default PricingCards;